import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { ChannelLogo } from '../components/channel-logo';
import { ChevronIcon } from '../components/icons';
import { FeedGrid } from '../components/feed-grid';
import {
  useChannelByIdQuery,
  useFollowChannelMutation,
  useSubscribedChannelsQuery,
  useUnfollowChannelMutation,
} from '../hooks/use-channels-query';
import { useChannelFeedArticles, useChannelFeedQuery } from '../hooks/use-feed-query';

export function ChannelFeed() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const channelId = id ?? '';

  const channel = useChannelByIdQuery(channelId || null);
  const subscribed = useSubscribedChannelsQuery();
  const feedQuery = useChannelFeedQuery(channelId);
  const articles = useChannelFeedArticles(channelId);
  const follow = useFollowChannelMutation();
  const unfollow = useUnfollowChannelMutation();

  const isFollowing = useMemo(() => {
    return (subscribed.data ?? []).some((c) => c.id === channelId);
  }, [subscribed.data, channelId]);

  const c = channel.data;

  return (
    <div className="nw-app nw-app--two">
      <aside className="nw-side">
        <div className="nw-side__label">{t('web.channelFeed.sidebar.label')}</div>
        <button type="button" className="nw-side__item" onClick={() => navigate('/home')}>
          <ChevronIcon dir="left" size={16} />
          <span className="nw-side__item-label">{t('web.channelFeed.sidebar.backToFeed')}</span>
        </button>
      </aside>
      <section className="nw-feed">
        <div className="nw-channel-header">
          {c ? (
            <ChannelLogo name={c.name} logoUrl={c.logoUrl ?? null} size={64} />
          ) : (
            <div className="nw-channel-header" style={{ width: 64, height: 64, padding: 0 }} />
          )}
          <div className="nw-channel-header__body">
            <h1 className="nw-channel-header__name">{c?.name ?? t('web.channelFeed.loadingChannel')}</h1>
            <div className="nw-channel-header__meta">
              {c?.language?.toUpperCase()}
              {c?.country ? ` · ${c.country}` : ''}
              {c?.category ? ` · ${c.category}` : ''}
            </div>
            {c?.description && <p className="nw-channel-header__desc">{c.description}</p>}
          </div>
          {channelId && (
            <button
              type="button"
              className={`nw-channel-row__follow ${isFollowing ? 'is-following' : ''}`}
              disabled={follow.isPending || unfollow.isPending}
              onClick={() => {
                if (isFollowing) unfollow.mutate(channelId);
                else follow.mutate(channelId);
              }}
            >
              {isFollowing ? t('web.explore.follow.following') : t('web.explore.follow.follow')}
            </button>
          )}
        </div>

        <FeedGrid
          articles={articles}
          isLoading={feedQuery.isLoading}
          isError={feedQuery.isError}
          emptyMessage={t('web.channelFeed.empty')}
          hasNextPage={feedQuery.hasNextPage}
          fetchNextPage={() => feedQuery.fetchNextPage()}
          isFetchingNextPage={feedQuery.isFetchingNextPage}
        />
      </section>
    </div>
  );
}
